import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import measurementService from '../../../services/measurementService';
import { getMeasurementErrorMessage } from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import { normalizePeriodName } from '../../../utils/scheduleTargetUtils';
import { determinePeriodFromTime, getInstantPeriodOptions, PERMANENT_END_TIMES, PERMANENT_START_TIMES } from '../../../utils/measurementPeriodUtils';
import AddFormModal from '../../../components/AddFormModal/AddFormModal';
import AppDateInput from '../../../components/Common/AppDateInput/AppDateInput';
import './AddWForm.css';

const resolveFallbackWeightPeriod = (timeValue) => {
  const [hStr = '0', mStr = '0'] = String(timeValue || '').split(':');
  const hours = Number.parseInt(hStr, 10);
  const minutes = Number.parseInt(mStr, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
  const total = (hours * 60) + minutes;
  if (total >= 360 && total <= 719) return 'Morning';
  if (total >= 720 && total <= 1079) return 'Afternoon';
  return 'Evening';
};

const AddWForm = ({ isOpen, onClose, practiceId, patientId, onSuccess, initialScheduleTargets = null }) => {
  const [loading, setLoading] = useState(false);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [isAutoPeriod, setIsAutoPeriod] = useState(true);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    period: '',
    weight: '',
    body_fat: '',
    bmi: '',
    note: ''
  });

  const resolveAutoWeightPeriod = (timeValue, targets) => {
    const determined = determinePeriodFromTime(
      timeValue,
      targets?.weightTimePeriod || [],
      PERMANENT_START_TIMES.weight,
      PERMANENT_END_TIMES.weight
    );
    return determined || resolveFallbackWeightPeriod(timeValue);
  };

  useEffect(() => {
    if (!isOpen || !practiceId) return;
    setIsAutoPeriod(true);
    setErrors({});
    setFormData({
      date: '',
      time: '',
      period: '',
      weight: '',
      body_fat: '',
      bmi: '',
      note: ''
    });
    if (initialScheduleTargets && typeof initialScheduleTargets === 'object') {
      setScheduleTargets(initialScheduleTargets);
    } else {
      fetchScheduleTargets();
    }
  }, [isOpen, practiceId, patientId, initialScheduleTargets]);

  useEffect(() => {
    if (!isAutoPeriod) return;
    const determined = formData.time ? resolveAutoWeightPeriod(formData.time, scheduleTargets) : '';
    setFormData((prev) => (prev.period === determined ? prev : { ...prev, period: determined }));
  }, [scheduleTargets, isAutoPeriod, formData.time]);

  const fetchScheduleTargets = async () => {
    try {
      const effectiveTargets = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(effectiveTargets);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const resolvedPeriod = isAutoPeriod
      ? (resolveAutoWeightPeriod(formData.time, scheduleTargets) || '')
      : formData.period;

    if (isAutoPeriod && resolvedPeriod !== formData.period) {
      setFormData((prev) => ({ ...prev, period: resolvedPeriod }));
    }

    const newErrors = {};
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!resolvedPeriod) newErrors.period = 'Period is required';
    if (!formData.weight) newErrors.weight = 'Weight is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setLoading(true);
    try {
      const selectedPeriod = uniquePeriods.find(
        (p) => normalizePeriodName(p.name) === normalizePeriodName(resolvedPeriod)
      );
      const periodId = selectedPeriod?.id ?? null;
      const response = await measurementService.createWeightMeasurement(practiceId, patientId, {
        measurement_date: formData.date,
        measurement_time: formData.time,
        period_id: periodId,
        period_name: resolvedPeriod,
        weight: parseFloat(formData.weight),
        body_fat: formData.body_fat ? parseFloat(formData.body_fat) : null,
        bmi: formData.bmi ? parseFloat(formData.bmi) : null,
        note: formData.note || null
      });
      toast.success('Weight measurement added successfully');
      const createdMeasurement = {
        id: response?.data?.data?.id || null,
        measurement_date: response?.data?.data?.measurement_date || formData.date,
        measurement_time: response?.data?.data?.measurement_time || formData.time,
        period_name: response?.data?.data?.period_name || formData.period,
        weight: response?.data?.data?.weight ?? parseFloat(formData.weight),
        body_fat: response?.data?.data?.body_fat ?? (formData.body_fat ? parseFloat(formData.body_fat) : null),
        bmi: response?.data?.data?.bmi ?? (formData.bmi ? parseFloat(formData.bmi) : null),
        note: response?.data?.data?.note ?? (formData.note || null)
      };
      onSuccess?.(createdMeasurement);
      window.dispatchEvent(
        new CustomEvent('weight:measurement-added', {
          detail: {
            practiceId: String(practiceId),
            patientId: String(patientId),
            measurement: createdMeasurement
          }
        })
      );
      window.dispatchEvent(
        new CustomEvent('measurement:added', {
          detail: {
            practiceId: String(practiceId),
            patientId: String(patientId),
            type: 'weight'
          }
        })
      );
      onClose();
    } catch (error) {
      toast.error(getMeasurementErrorMessage(error, 'Failed to add measurement'));
    } finally {
      setLoading(false);
    }
  };

  const uniquePeriods = useMemo(
    () => getInstantPeriodOptions(scheduleTargets?.weightTimePeriod || [], PERMANENT_START_TIMES.weight),
    [scheduleTargets]
  );

  const handleTimeChange = (timeValue) => {
    setFormData((prev) => {
      if (!isAutoPeriod) {
        return { ...prev, time: timeValue };
      }
      const determined = resolveAutoWeightPeriod(timeValue, scheduleTargets) || '';
      return { ...prev, time: timeValue, period: determined };
    });
  };

  return (
    <AddFormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Add Weight"
      footer={(
        <div className="add-practice-form-actions">
          <button type="button" className="add-practice-btn add-practice-btn-cancel" onClick={onClose} disabled={loading}>Close</button>
          <button type="submit" className="add-practice-btn add-practice-btn-add" disabled={loading}>{loading ? 'Saving...' : 'Add'}</button>
        </div>
      )}
    >
      <section className="aw-section">
        <h6 className="aw-section-title">Weight</h6>
        <div className="aw-grid">
          <div className="aw-field"><label>Date <span className="aw-req">*</span></label><AppDateInput value={formData.date} onChange={(v) => setFormData((p) => ({ ...p, date: v }))} />{errors.date && <div className="aw-error">{errors.date}</div>}</div>
          <div className="aw-field"><label>Time <span className="aw-req">*</span></label><input type="time" value={formData.time} onChange={(e) => handleTimeChange(e.target.value)} />{errors.time && <div className="aw-error">{errors.time}</div>}</div>
          <div className="aw-field"><label>Period <span className="aw-req">*</span></label><select value={isAutoPeriod ? 'AUTO' : formData.period} onChange={(e) => { const v = e.target.value; if (v === 'AUTO') { setIsAutoPeriod(true); setFormData((p) => ({ ...p, period: resolveAutoWeightPeriod(p.time, scheduleTargets) || '' })); } else { setIsAutoPeriod(false); setFormData((p) => ({ ...p, period: v })); } }}><option value="AUTO">Automatically determine Measurement Period</option>{uniquePeriods.map((p) => <option key={p.id || p.name} value={p.name}>{p.name}</option>)}</select>{errors.period && <div className="aw-error">{errors.period}</div>}</div>
          <div className="aw-field"><label>Weight <span className="aw-req">*</span></label><input type="number" step="0.1" value={formData.weight} onChange={(e) => setFormData((p) => ({ ...p, weight: e.target.value }))} />{errors.weight && <div className="aw-error">{errors.weight}</div>}</div>
          <div className="aw-field"><label>BMI</label><input type="number" step="0.1" value={formData.bmi} onChange={(e) => setFormData((p) => ({ ...p, bmi: e.target.value }))} /></div>
          <div className="aw-field"><label>Body Fat</label><input type="number" step="0.1" value={formData.body_fat} onChange={(e) => setFormData((p) => ({ ...p, body_fat: e.target.value }))} /></div>
        </div>
        <div className="aw-field"><label>Note</label><textarea rows="2" value={formData.note} onChange={(e) => setFormData((p) => ({ ...p, note: e.target.value }))} /></div>
      </section>
    </AddFormModal>
  );
};

export default AddWForm;
